/**
 * Product page behavior (sections/product.liquid): thumbnail gallery, full-screen image viewer (a modal
 * dialog with a scrim; arrows/←/→ to browse, Esc or a click outside to close), quantity stepper, variant select
 * (reloads the page on ?variant= so price, availability and SKU are rendered by Shopify) and the tabs.
 */
class ProductPage extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb) return this.showSlide([...this.querySelectorAll('[data-gallery-thumb]')].indexOf(thumb));

      if (event.target.closest('[data-lightbox-open]')) return this.openLightbox();
      if (event.target.closest('[data-lightbox-close]')) return this.lightbox?.close();
      if (event.target.closest('[data-lightbox-prev]')) return this.stepLightbox(-1);
      if (event.target.closest('[data-lightbox-next]')) return this.stepLightbox(1);
      // Clicking the scrim (not the photo or a button) closes the viewer.
      if (event.target === this.lightbox || event.target.matches('[data-lightbox-stage]')) return this.lightbox.close();

      const tab = event.target.closest('[data-tab]');
      if (tab) return this.showTab(tab.dataset.tab);

      const input = this.querySelector('.quantity-stepper__input');
      if (!input) return;
      if (event.target.closest('[data-quantity-minus]')) this.setQuantity(input, (parseInt(input.value, 10) || 1) - 1);
      else if (event.target.closest('[data-quantity-plus]')) this.setQuantity(input, (parseInt(input.value, 10) || 1) + 1);
    });

    this.addEventListener('input', (event) => {
      if (event.target.matches('.quantity-stepper__input')) this.updateAvailability(event.target);
    });

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-variant-select]')) {
        window.location.href = `${this.dataset.productUrl}?variant=${encodeURIComponent(event.target.value)}`;
      } else if (event.target.matches('.quantity-stepper__input')) {
        this.setQuantity(event.target, parseInt(event.target.value, 10) || 1);
      }
    });

    this.lightbox = this.querySelector('[data-lightbox]');
    this.lightbox?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.stepLightbox(-1);
      else if (event.key === 'ArrowRight') this.stepLightbox(1);
    });
    this.lightbox?.addEventListener('close', () => {
      this.showSlide(this.lightboxIndex);
      this.querySelector('[data-lightbox-open]')?.focus();
    });

    const first = this.querySelector('[data-tab][data-default]') || this.querySelector('[data-tab]');
    if (first) this.showTab(first.dataset.tab);
  }

  setQuantity(input, value) {
    input.value = Math.max(1, value);
    this.updateAvailability(input);
  }

  /** Over what is on hand: the pill says "Available to Order" (the order still goes through). */
  updateAvailability(input) {
    const pill = this.querySelector('[data-availability-pill]');
    if (!pill) return;
    const label = pill.querySelector('[data-availability-label]');
    if (!pill.dataset.labelOriginal) pill.dataset.labelOriginal = label.textContent.trim();
    const over = pill.dataset.status !== 'order' && (parseInt(input.value, 10) || 1) > parseInt(pill.dataset.onHand, 10);
    pill.classList.toggle('availability-pill--order', over || pill.dataset.status === 'order');
    pill.classList.toggle('availability-pill--in', !over && pill.dataset.status === 'in');
    pill.classList.toggle('availability-pill--low', !over && pill.dataset.status === 'low');
    label.textContent = over ? pill.dataset.labelOrder : pill.dataset.labelOriginal;
  }

  showSlide(index) {
    this.querySelectorAll('[data-gallery-slide]').forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
  }

  openLightbox() {
    if (!this.lightbox) return;
    const slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.lightboxIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.showLightboxImage();
    this.lightbox.showModal();
  }

  stepLightbox(direction) {
    const count = this.querySelectorAll('[data-gallery-slide]').length;
    this.lightboxIndex = (this.lightboxIndex + direction + count) % count;
    this.showLightboxImage();
  }

  showLightboxImage() {
    const slides = this.querySelectorAll('[data-gallery-slide]');
    const slide = slides[this.lightboxIndex];
    const image = this.lightbox.querySelector('[data-lightbox-image]');
    image.src = slide.dataset.full;
    image.alt = slide.dataset.alt || '';
    const count = this.lightbox.querySelector('[data-lightbox-count]');
    if (count) count.textContent = `${this.lightboxIndex + 1} / ${slides.length}`;
  }

  showTab(name) {
    this.querySelectorAll('[data-tab]').forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.tab === name)));
    this.querySelectorAll('[data-panel]').forEach((panel) => (panel.hidden = panel.dataset.panel !== name));
  }
}
customElements.define('product-page', ProductPage);
